import type { ApolloCache } from "@apollo/client";

import {
  insertIntoConnection,
  removeFromConnection,
} from "#app/lib/data/connection-cache-utils";

// ═══════════════════════════════════════════════════════════════════════════════
// Note Cache Utilities
// ═══════════════════════════════════════════════════════════════════════════════
// Apollo cache manipulation helpers for note mutations.

// `activities` is offset-paginated (a cache entry per skip/limit), and the
// activity feed pages through it — so BOTH filters below are load-bearing:
// the issue id picks this issue's feed, and `"skip":0` picks its first page.
// Dropping the skip filter writes the edge into every page the viewer has
// loaded, which prepends the row mid-feed and inflates `total` once per page.
// Rows that arrive while the viewer has paged past the first page land in the
// page-0 entry the paging hook isn't currently watching, so they surface on
// its next refetch rather than live — correct-but-late, which is the tradeoff
// every offset-paginated connection in the app makes.
const FIRST_PAGE = '"skip":0';

/**
 * Add an activity to the ActivityViewData activities cache (prepended).
 * Dedupe (mutation handlers and the activityChanged subscription can both
 * process the same row) and the `total` bump come from the shared connection
 * helper; the normalized entity write carries the field updates.
 */
export function addActivityToCache(
  cache: ApolloCache,
  issueId: string,
  activity: { activityId?: string } | null | undefined,
) {
  if (!activity?.activityId || !issueId) return;

  insertIntoConnection(
    cache,
    "activities",
    "ActivityEdge",
    { __typename: "Activity", activityId: activity.activityId },
    { position: "start", storeFieldNameIncludes: [FIRST_PAGE, issueId] },
  );
}

/**
 * Remove an activity from the ActivityViewData activities cache — the
 * activityChanged `deleted` case (a merge window's changes reverted to the
 * original, deleting the now-empty row server-side).
 *
 * Same first-page scoping as the insert, for the same reason: `total` must be
 * decremented once, not once per loaded page. The row is a real server-side
 * delete, so it also evicts — `offsetPagination` drops dangling refs on read,
 * which clears the edge from any other page without touching their counts.
 */
export function removeActivityFromCache(
  cache: ApolloCache,
  issueId: string,
  activityId: string,
) {
  if (!activityId || !issueId) return;

  removeFromConnection(
    cache,
    "activities",
    { __typename: "Activity", activityId },
    { storeFieldNameIncludes: [FIRST_PAGE, issueId], evict: true },
  );
}

/**
 * Add a parent note to the ActivityViewData notes cache.
 * Appends to the end of the notes connection.
 */
export function addNoteToCache(
  cache: ApolloCache,
  issueId: string,
  note: any,
) {
  if (!note || !issueId) return;

  cache.modify({
    fields: {
      notes(existingConnection, { storeFieldName, toReference }) {
        if (!storeFieldName.includes(issueId)) return existingConnection;

        const newNoteRef = toReference(note);
        if (!newNoteRef) return existingConnection;

        return {
          ...existingConnection,
          edges: [
            ...(existingConnection?.edges || []),
            { __typename: "NoteEdge", node: newNoteRef },
          ],
          total: (existingConnection?.total || 0) + 1,
        };
      },
    },
  });
}

/**
 * Add a reply to a parent note's replies connection.
 * Also increments the parent's replyCount.
 */
export function addReplyToCache(
  cache: ApolloCache,
  parentNoteId: string,
  reply: any,
) {
  if (!reply || !parentNoteId) return;

  // `identify` answers `undefined` for an object it cannot key, and the cache
  // reads an absent `id` as the ROOT — the edit would land on ROOT_QUERY.
  const parentId = cache.identify({ __typename: "Note", noteId: parentNoteId });
  if (!parentId) return;

  cache.modify({
    id: parentId,
    fields: {
      replyCount(existing = 0) {
        return existing + 1;
      },
      replies(existingReplies, { toReference }) {
        const newReplyRef = toReference(reply);
        if (!newReplyRef) return existingReplies;

        return {
          ...existingReplies,
          edges: [
            ...(existingReplies?.edges || []),
            { __typename: "NoteEdge", node: newReplyRef },
          ],
          total: (existingReplies?.total || 0) + 1,
        };
      },
    },
  });
}

/**
 * Remove a note from the ActivityViewData notes cache.
 */
export function removeNoteFromCache(
  cache: ApolloCache,
  issueId: string,
  noteId: string,
) {
  if (!noteId || !issueId) return;

  cache.modify({
    fields: {
      notes(existingConnection, { storeFieldName, readField }) {
        if (!storeFieldName.includes(issueId)) return existingConnection;

        return {
          ...existingConnection,
          edges: existingConnection.edges.filter((edge: any) => {
            const id = readField("noteId", edge.node);
            return id !== noteId;
          }),
          total: Math.max((existingConnection?.total || 0) - 1, 0),
        };
      },
    },
  });
}

/**
 * Move the issue's `noteCount` (the card's comment count — top-level comments
 * only) by `delta`, so a board or list card agrees with the thread at once.
 */
export function adjustIssueNoteCount(
  cache: ApolloCache,
  issueId: string,
  delta: number,
) {
  if (!issueId) return;
  const id = cache.identify({ __typename: "Issue", issueId });
  if (!id) return;
  cache.modify({
    id,
    fields: {
      noteCount(existing: number = 0) {
        return Math.max(existing + delta, 0);
      },
    },
  });
}
